import * as Sentry from "@sentry/react";

/**
 * Detect transient/non-actionable errors that bubble up as unhandled promise
 * rejections — chiefly axios failures that never got a `.catch()` at the call
 * site (e.g. redux-promise-middleware re-rejecting a dispatched thunk).
 *
 * These are environmental (offline, dropped connection, timeout, canceled
 * request), not bugs in our code, so they should not be reported to Sentry.
 *
 * @param error The rejection reason / thrown value
 * @returns true when the error is a transient network failure we can swallow
 */
export const isTransientNetworkError = (error) => {
  if (!error || typeof error !== "object") {
    return false;
  }

  // axios tags its errors; code is the most reliable signal in axios v1.
  const transientCodes = [
    "ERR_NETWORK", // "Network Error" — no response reached the browser
    "ERR_CANCELED", // request aborted (e.g. CancelToken / unmount)
    "ECONNABORTED", // timeout
    "ETIMEDOUT",
  ];

  if (error.code && transientCodes.indexOf(error.code) !== -1) {
    return true;
  }

  // A canceled request is re-wrapped by throwCatchedError() (see
  // app/services/utils.js) as a bare object:
  //   { response: { status: 700, message: "Operation canceled by the user." } }
  // It carries no axios markers, no `message` at the top level and no stack, so
  // Sentry logs it as "Object captured as promise rejection with keys: response".
  // Match it on the app's internal cancellation sentinel.
  if (
    error.response &&
    typeof error.response === "object" &&
    (error.response.status === 700 ||
      error.response.message === "Operation canceled by the user.")
  ) {
    return true;
  }

  if (error.isAxiosError && !error.response) {
    // An axios error with no response never reached the server.
    return true;
  }

  return error.message === "Network Error";
};

/**
 * Install a single global `unhandledrejection` handler so a missing `.catch()`
 * on a network request no longer floods Sentry (and the console) with
 * AxiosError "Network Error" noise. Transient network rejections are logged and
 * suppressed; everything else is left untouched so real bugs still surface.
 */
const installGlobalUnhandledRejectionHandler = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.addEventListener("unhandledrejection", (event) => {
    if (isTransientNetworkError(event.reason)) {
      // Stop the browser's default unhandled-rejection logging.
      event.preventDefault();
      console.warn(
        "Suppressed unhandled network rejection:",
        event.reason && event.reason.message,
      );
    }
  });
};

/**
 * Utility function to observe redux store updates
 * @see https://github.com/reduxjs/redux/issues/303#issuecomment-125184409
 *
 * @param store redux store
 * @param select a selector function to access a specific part of the store
 * @param onChange a callback that receive as first parameter the selected part of the store
 */
const observeStore = (store, select, onChange) => {
  let currentState;

  const handleChange = () => {
    let nextState = select(store.getState());
    if (nextState !== currentState) {
      currentState = nextState;
      onChange(currentState);
    }
  };

  let unsubscribe = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
};

/**
 * Init and Configure Sentry Crash Reporting
 *
 * @param store Redux store
 * @param config Configuration
 */
const doInitSentry = (store, config) => {
  const {
    APP_ENV,
    APP_NAME,
    APP_RELEASE,
    APP_SENTRY_DSN
  } = config;

  // Disable Sentry in local env
  if (["local", "develop"].indexOf(APP_ENV) != -1) {
    return;
  }

  // Initial Configuration
  Sentry.init({
    dsn: APP_SENTRY_DSN,
    debug: APP_ENV !== "production",
    environment: APP_ENV,
    release: `${APP_NAME}@${APP_RELEASE}`,
    //transport: Sentry.Transports.XHRTransport,
    integrations: defaultIntegrations => {
      // Remove default ReportingObserver integration
      const customIntegrations = defaultIntegrations.filter(
        integration => integration.name !== "ReportingObserver"
      );

      // For now, disable reporting type "deprecation" and "intervention"
      return [
        ...customIntegrations,
      ];
    },
    ignoreErrors: [
      "Error: Request failed with status code 404",
      "Operation canceled by the user."
    ],
    // Drop transient network failures (e.g. uncaught axios rejections) so they
    // don't create noise. The global unhandledrejection handler below swallows
    // them at the source; this is a safety net in case Sentry's own global
    // handler captures the event before our listener runs.
    beforeSend: (event, hint) => {
      if (isTransientNetworkError(hint && hint.originalException)) {
        return null;
      }
      return event;
    }
  });

  // Catch-all for promise rejections lacking a `.catch()` (see helper above).
  installGlobalUnhandledRejectionHandler();

  // Observe user
  observeStore(
    store,
    state => state.auth.user,
    user => {
      Sentry.setUser(
        user
          ? { id: user.id, username: user.mainEmail, email: user.mainEmail }
          : null
      );
    }
  );

  // Observe language
  observeStore(
    store,
    state => state.params.lng,
    lng => {
      Sentry.setTag("ttp:lng", lng);
    }
  );

  // Observe Community
  observeStore(
    store,
    state => state.auth.currentClient,
    currentClient => {
      Sentry.setTag("ttp:client.id", currentClient && currentClient.id);
      Sentry.setTag(
        "ttp:client.name",
        currentClient && (currentClient.abbreviation || currentClient.name)
      );
    }
  );
};

/**
 * Init and Configure Sentry Crash Reporting
 *
 * @param store Redux store
 * @param config {
 *      APP_ENV: The app env (local, dev, ...)
 *      APP_NAME: The app name
 *      APP_RELEASE: The current app release
 *      APP_SENTRY_DSN: The project DSN in sentry
 * }
 */
export const initSentry = (store, config) => {
  try {
    doInitSentry(store, config);
  } catch (e) {
    // Sentry may not be initialized here, so captureException would be a no-op
    console.error("Sentry init failed", e);
  }
};
